import { TimelapseOutlined } from "@mui/icons-material";
import { FC, useContext, useEffect, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import { useGlobalEffectActions, usePageContext } from "@ryde/redux";
import {
  liveMapPlannedForNowAtomFamily,
  TOOLTIP_DELAY_BUTTONS,
} from "@ryde/shared";

import { LiveMapContext } from "../../../context";
import { useLiveMapToolbarStats } from "../use-live-map-toolbar-stats";
import { LiveMapPlannedForNowTooltipStyled } from "./live-map.grid-status-breakdown.styled";
import { LiveMapGridStatusChipTooltipTriggerStyled } from "./live-map.grid-status-chip.styled";
import { LiveMapPlannedForNowChipButtonStyled } from "./live-map.planned-for-now-chip.styled";

interface ILiveMapPlannedForNowToggleProps {
  isMobileStrip?: boolean;
}

export const LiveMapPlannedForNowToggle: FC<
  ILiveMapPlannedForNowToggleProps
> = ({ isMobileStrip = false }) => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { resetPaginationAppEvent } = useGlobalEffectActions();
  const { plannedForNowCount } = useLiveMapToolbarStats();
  const { fetchRidesWithLoader } = useContext(LiveMapContext);
  const [isEnabled, setIsEnabled] = useRecoilState(
    liveMapPlannedForNowAtomFamily(entity),
  );
  const [optimisticEnabled, setOptimisticEnabled] = useState<boolean | null>(
    null,
  );
  const [, startTransition] = useTransition();

  const effectiveEnabled = optimisticEnabled ?? isEnabled;

  useEffect(() => {
    if (optimisticEnabled !== null && optimisticEnabled === isEnabled) {
      setOptimisticEnabled(null);
    }
  }, [isEnabled, optimisticEnabled]);

  const handleToggle = useEvent(() => {
    const next = !effectiveEnabled;

    setOptimisticEnabled(next);

    startTransition(() => {
      setIsEnabled(next);
      resetPaginationAppEvent();
      fetchRidesWithLoader?.();
    });
  });

  const tooltip = t("rideMap.PLANNED_FOR_NOW_TOOLTIP");
  const label = t("rideMap.PLANNED_FOR_NOW");

  return (
    <FramerTooltip
      content={
        <LiveMapPlannedForNowTooltipStyled>
          {tooltip}
        </LiveMapPlannedForNowTooltipStyled>
      }
      mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
      maxWidth="480px"
      fitMaxWidthToBounds={false}
      fitMaxHeightToBounds={false}
    >
      <LiveMapGridStatusChipTooltipTriggerStyled
        className={
          isMobileStrip
            ? "live-map-grid-status-chip-tooltip-trigger"
            : undefined
        }
      >
        <LiveMapPlannedForNowChipButtonStyled
          $isOn={effectiveEnabled}
          aria-pressed={effectiveEnabled ? "true" : "false"}
          aria-label={tooltip}
          onClick={handleToggle}
        >
          <TimelapseOutlined className="live-map-planned-for-now-chip-icon" />
          <span className="live-map-planned-for-now-chip-label">{label}</span>
          <span className="live-map-planned-for-now-chip-count">
            {plannedForNowCount}
          </span>
        </LiveMapPlannedForNowChipButtonStyled>
      </LiveMapGridStatusChipTooltipTriggerStyled>
    </FramerTooltip>
  );
};
